import type React from "react";

import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { getActionButtonColorStyles } from "shared/atoms/BaseButton/BaseButton";

type Button = {
  value: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  iconActive?: React.ReactNode;
  link?: string;
  disabled?: boolean;
};

interface Props {
  readonly group: Button[];
  active?: string;
  setActive?: (value: string) => void;
}

export const ButtonsGroup: React.FC<Props> = ({ group, active, setActive }) => {
  return (
    <Container>
      {group.map(button => {
        const isActive = button.value === active;
        const iconToShow = isActive && button.iconActive ? button.iconActive : button.icon;

        return (
          <ButtonWrapper
            key={button.value}
            data-testid={`invite-type-${button.value}`}
            className={clsx({
              active: isActive,
              disabled: button?.disabled,
            })}
            onClick={() => !button?.disabled && setActive?.(button.value)}
            as={button.link ? Link : "div"}
            to={button.link}
          >
            {iconToShow && <IconWrapper>{iconToShow}</IconWrapper>}
            <Typography variant="subtitle1" noWrap>
              {button.label}
            </Typography>
          </ButtonWrapper>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
`;

const ButtonWrapper = styled(Box)`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 11px 16px;
  box-sizing: border-box;
  letter-spacing: 0.5px;
  cursor: pointer;
  text-decoration: none;
  transition: 0.2s;

  & .MuiTypography-root {
    transition: inherit;
  }

  &:first-child {
    border-radius: 8px 0 0 8px;
  }
  &:last-child {
    border-radius: 0 8px 8px 0;
  }

  &:hover {
    & .MuiTypography-root {
      color: ${props => props.theme.colors.redesign.b100};
    }
  }

  &.active {
    & .MuiTypography-root {
      color: ${props => props.theme.colors.redesign.b100};
    }
  }

  &.disabled {
    background: ${({ theme }) => theme.colors.redesign.db5};
    color: ${({ theme }) => theme.colors.redesign.db40};
    pointer-events: none;
  }

  ${getActionButtonColorStyles()}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  svg {
    width: 16px;
    height: 16px;
  }
`;
